import { useMemo } from 'react';
import { mergeFrameworkAnswers } from '../utils/dashboardHelpers';

/**
 * Derives the memoized selectors that feed the progress-trend loaders: the deduped company
 * question list, the per-user source mapping, the location-filtered user ids, the location
 * label map, the merged (answered) framework answer set, and the CUSTOM-expanded question
 * lists used for the by-location and by-period trend charts.
 */
export const useDashboardSelectors = ({
  reportingQuestions,
  allUsers,
  selectedLocations,
  selectedModules,
  selectedPeriods,
  selectedPeriodsValue,
  locationOptions,
  periodOptions,
  reportingAnswers,
}) => {
  const totalQuestionForCompany = useMemo(() => {
    const list = [];
    const uniqueQuestionSet = new Set();
    if (Array.isArray(reportingQuestions)) {
      reportingQuestions.forEach(item => {
        const key = `${item.questionId}-${item.moduleId}-${item.frequency}`;
        if (!uniqueQuestionSet.has(key)) {
          uniqueQuestionSet.add(key);
          try {
            list.push({
              questionId: item.questionId,
              moduleId: item.moduleId,
              frequency: item.frequency,
              questionType: item.questionType,
              frameworksIds: JSON.parse(item.mapFrameworkIds || '[]')
            });
          } catch (e) {
            list.push({
              questionId: item.questionId,
              moduleId: item.moduleId,
              frequency: item.frequency,
              questionType: item.questionType,
              frameworksIds: []
            });
          }
        }
      });
    }
    return list;
  }, [reportingQuestions]);

  const userResult = useMemo(() => {
    if (!Array.isArray(allUsers)) return [];
    return allUsers.map(user => {
      let sourceId = [];
      try {
        if (user.source_ids) {
          sourceId = JSON.parse(user.source_ids);
        }
      } catch (e) {
        console.warn(`Invalid source_ids for user ID ${user.id}: ${user.source_ids}`);
      }
      return {
        id: user.id,
        sourceId
      };
    });
  }, [allUsers]);

  const filteredUsers = useMemo(() => {
    return selectedLocations.length === 0
      ? userResult.map(item => item.id)
      : userResult.filter(user =>
        user.sourceId.some(sid => selectedLocations.includes(sid))
      ).map(item => item.id);
  }, [selectedLocations, userResult]);

  const locationMap = useMemo(() =>
    locationOptions.reduce((acc, curr) => {
      acc[curr.value] = curr.label;
      return acc;
    }, {}),
    [locationOptions]
  );

  const customQuestionIds = useMemo(() =>
    totalQuestionForCompany
      .filter(item => item.frequency === "CUSTOM")
      .map(item => item.questionId),
    [totalQuestionForCompany]
  );

  const questionIdsOthers = useMemo(() =>
    totalQuestionForCompany
      .filter(item => item.frequency === "EVERY_FY" || item.frequency === "ONE_TIME")
      .map(item => item.questionId),
    [totalQuestionForCompany]
  );

  const fromDates = useMemo(() =>
    selectedPeriodsValue.map(p => p.fromDate),
    [selectedPeriodsValue]
  );

  const mergedFrameworkAnswers = useMemo(() => {
    const effectiveLocationIds = selectedLocations.length === 0
      ? locationOptions.map(o => o.value)
      : selectedLocations;
    return mergeFrameworkAnswers(reportingAnswers, questionIdsOthers, customQuestionIds, fromDates, effectiveLocationIds);
  }, [reportingAnswers, questionIdsOthers, selectedLocations, customQuestionIds, fromDates, locationOptions]);

  const filteredQuestionsForProgressTrends = useMemo(() => {
    const list = [];
    for (const q of totalQuestionForCompany) {
      const isModuleMatched = selectedModules.length === 0 || selectedModules.includes(q.moduleId);
      if (isModuleMatched) {
        let check1 = selectedLocations.length === 0 ? locationOptions.length : selectedLocations.length;
        const weight = q.frequency === "CUSTOM" ? check1 : 1;
        if (weight > 0) {
          list.push({ ...q, weight });
        }
      }
    }
    return list;
  }, [totalQuestionForCompany, selectedModules, selectedLocations, locationOptions]);

  const filteredQuestionsForProgressLocation = useMemo(() => {
    const list = [];
    for (const q of totalQuestionForCompany) {
      const isModuleMatched = selectedModules.length === 0 || selectedModules.includes(q.moduleId);
      if (isModuleMatched) {
        let check1 = selectedPeriods.length === 0 ? periodOptions.length : selectedPeriods.length;
        const weight = q.frequency === "CUSTOM" ? check1 : 1;
        if (weight > 0) {
          list.push({ ...q, weight });
        }
      }
    }
    return list;
  }, [totalQuestionForCompany, selectedModules, selectedPeriods, periodOptions]);

  return {
    userResult,
    filteredUsers,
    locationMap,
    mergedFrameworkAnswers,
    filteredQuestionsForProgressTrends,
    filteredQuestionsForProgressLocation,
  };
};
